/* The guide: a little 3D book who shows visitors around the landing page and students around the planner (see
   book3d.ts). This part decides who gets him and loads him, and three.js (/vendor/three.module.min.js), after the page
   has loaded, so nobody else downloads any of it.
   He's on for everyone, except:
   - ?mascot=0 turns him off in that browser (and ?mascot turns him back on);
   - a device that asks for less motion, or saves data, or has no WebGL;
   - test scripts driving a browser, unless they ask (window.__PA_MASCOT).
   "Hide" in his menu sends him away for the rest of the visit (a little book button brings him back). */
export type Where = 'landing' | 'planner';
export type NewsKind = 'offer' | 'offerCond' | 'submitted' | 'accepted';
export type Pick = { name: string };
export type Guide = {
  /* planner: a screen opened (a tab, the questions, the globe); quiet: a busy screen, where he steps out of the way */
  screen(name: string, quiet?: boolean): void;
  /* planner: a guided tour's step (what it circles), or null when it ends; he presents it */
  tourStep(step: { el: Element | null } | null): void;
  /* planner: news about an application */
  news(kind: NewsKind, school?: string): void;
  /* landing: a university picked on the globe (null: the breakdown closed) */
  pick(p: Pick | null): void;
  /* landing: out of the way ("Let's begin" dives into the globe), or back (false: the page came back as it was left) */
  hold(on?: boolean): void;
  /* gone for good (the page is changing) */
  destroy(): void;
};
/* the planner's own guided tours, which his menu starts: this page's, or the full tour (each false if there's none
   here, and he explains the screen himself instead) */
export type Actions = { explain?: () => boolean; tour?: () => boolean };
export type Start = (where: Where, act: Actions) => Promise<Guide | null>;

type Env = { search: string; stored: string | null; reduced: boolean; webgl: boolean; saveData: boolean; webdriver: boolean; asked: boolean };

/* ?mascot / ?mascot=1 on, ?mascot=0 (or off, no) off; null if the address doesn't say */
export function askedFor(search: string): boolean | null {
  const m = search.match(/[?&]mascot(?:=([^&]*))?(?:&|$)/);
  return m ? !/^(0|off|no|false)$/i.test(m[1] || '') : null;
}
/* whether this visitor gets him */
export function wanted(e: Env): boolean {
  const said = askedFor(e.search);
  const off = said === false || (said === null && e.stored === '0');
  if (off || e.reduced || !e.webgl || e.saveData) return false;
  if (e.webdriver && !e.asked) return false;
  return true;
}
const KEY = 'pa_mascot';
function envNow(): Env {
  let stored: string | null = null;
  const said = askedFor(location.search);
  try {
    if (said === false) localStorage.setItem(KEY, '0');
    if (said === true) localStorage.removeItem(KEY);
    stored = localStorage.getItem(KEY);
  } catch { /* private mode */ }
  const nav = navigator as Navigator & { connection?: { saveData?: boolean } };
  let webgl = false;
  try { webgl = typeof WebGLRenderingContext !== 'undefined' && !!document.createElement('canvas').getContext('webgl'); } catch { webgl = false; }
  return {
    search: location.search, stored, webgl,
    reduced: typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches,
    saveData: !!nav.connection?.saveData,
    webdriver: !!navigator.webdriver,
    asked: !!(window as unknown as { __PA_MASCOT?: unknown }).__PA_MASCOT,
  };
}

/* Starts him (after the page has loaded) if this visitor gets him. load: his module (book3d.ts), however this page
   gets it. Calls made before he's there are passed on once he is: only the latest screen, step and pick. */
export function mascot(where: Where, act: Actions, load: () => Promise<{ start: Start }>): Guide | null {
  if (!wanted(envNow())) return null;
  let real: Guide | null = null, failed = false, gone = false, timer = 0;
  const queue: [keyof Guide, unknown[]][] = [];
  const go = () => load().then(m => m.start(where, act)).then(g => {
    if (gone) { g?.destroy(); return; }
    real = g; failed = !g;
    if (g) for (const [k, a] of queue.splice(0)) (g[k] as (...x: unknown[]) => void)(...a);
  }).catch(e => { failed = true; console.warn('guide:', e); });
  const soon = () => { timer = window.setTimeout(go, 300); };
  if (document.readyState === 'complete') soon(); else addEventListener('load', soon, { once: true });
  const latestOnly: (keyof Guide)[] = ['screen', 'tourStep', 'pick'];
  const call = (k: keyof Guide) => (...a: unknown[]) => {
    if (gone) return;
    if (real) { (real[k] as (...x: unknown[]) => void)(...a); return; }
    if (failed) return;
    if (latestOnly.includes(k)) { const i = queue.findIndex(([n]) => n === k); if (i >= 0) queue.splice(i, 1); }
    queue.push([k, a]);
  };
  return {
    screen: call('screen'), tourStep: call('tourStep'), news: call('news'), pick: call('pick'), hold: call('hold'),
    destroy() {
      if (gone) return;
      gone = true; clearTimeout(timer); removeEventListener('load', soon);
      real?.destroy(); real = null; queue.length = 0;
    },
  };
}
